import React, { useRef } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { MobileSpacer } from 'components/mobile-spacer';
import { useKeyboardState } from 'hooks/use-keyboard-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

const DescriptionInput = styled.input`
  width: 100%;
  border: 1px solid ${Styles.color.grey.three};
  border-radius: ${Styles.borderRadius};
  box-shadow: ${Styles.boxShadow};
  resize: none;
  color: ${Styles.color.secondary};
  font-family: ${Styles.fontFamily.base};
  padding: 12px;
  font-size: 1rem;
  margin-top: 0.5rem;
`;

const ErrorMessage = styled.p`
  color: ${Styles.color.error};
  margin: 0;
  font-size: 0.75rem;
`;

const Legend = styled.legend`
  display: block;
  font-size: 1.2rem;
  font-weight: ${Styles.fontWeight.heavy};
  margin: 0 0 1rem 0;
`;

const DescriptionContainer = styled.fieldset`
  border: 0;
  margin-bottom: 1rem;
`;

declare interface IDescriptionChange {
  value: string;
  hasError: boolean;
}
interface IVehicleDescription {
  onChange: (change: IDescriptionChange) => void;
  hasError?: boolean;
  value?: string;
}

export const VehicleDescription: React.FC<IVehicleDescription> = ({
  onChange,
  value,
  hasError,
}) => {
  const { isKeyboardOpen, keyboardHeight } = useKeyboardState();
  const { formatMessage } = useIntl();
  const curbsideVehicleDescriptionMaxLength = useFlag(
    LaunchDarklyFlag.CURBSIDE_VEHICLE_DESCRIPTION_MAX_LENGTH
  );

  const errorMessage = formatMessage(
    { id: 'descriptionLengthError' },
    {
      min: 1,
      max: curbsideVehicleDescriptionMaxLength,
    }
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const description = event.target.value;
    const isValid = description && description.length <= curbsideVehicleDescriptionMaxLength;
    onChange({ value: description, hasError: !isValid });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13 && inputRef && inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <DescriptionContainer>
      <Legend>{formatMessage({ id: 'describeYourVehicle' })}</Legend>
      <label htmlFor="vehicle-description-input">
        {formatMessage({ id: 'curbsideVehicleDescription' })}
      </label>
      <DescriptionInput
        data-testid="vehicle-description"
        id="vehicle-description-input"
        placeholder={formatMessage({ id: 'curbsideVehicleDescriptionPlaceholder' })}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        required
        name="vehicleDescription"
        value={value}
        ref={inputRef}
      />
      {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
      {isKeyboardOpen && <MobileSpacer height={keyboardHeight} />}
    </DescriptionContainer>
  );
};
